import { Stack, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';

import { Card } from '@/components/layout/Card/Card';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import {
  DispositiveProvisions_BeneficiaryDesignationsFragment,
  DispositiveProvisions_DispositionScenarioFragment,
} from '@/modules/dispositiveProvisions/graphql/DispositiveProvisions.fragments.generated';
import { COLORS } from '@/styles/tokens/colors';

import { RowData } from './DispositionScenarioTable.types';
import {
  getBeneficiaryDesignationTableRows,
  getColumns,
} from './DispositionScenarioTable.utils';

interface DispositionScenarioTableProps {
  dispositionScenario?: DispositiveProvisions_DispositionScenarioFragment;
  primaryClients: {
    displayName: string;
    id: string;
  }[];
  /**
   * List of dispositions ordered by death event, can be null if we do not want to show the table
   */
  rowDataByDeath: [RowData[], RowData[]] | null;
  /**
   * When set, overrides the death-order DP rendering and renders the BDG
   * primary/contingent variant of the same table. Used for retirement /
   * DH-insurance accounts. Templates and the "<grantor> dies first"
   * sub-heading don't apply in this mode.
   */
  beneficiaryDesignations?: DispositiveProvisions_BeneficiaryDesignationsFragment | null;
}

interface SectionConfig {
  key: string;
  heading: string;
  rows: RowData[];
}

export function DispositionScenarioTable({
  dispositionScenario,
  rowDataByDeath,
  primaryClients,
  beneficiaryDesignations,
}: DispositionScenarioTableProps) {
  const theme = useTheme();
  const isBdg = beneficiaryDesignations !== undefined;
  const columns = useMemo(() => getColumns(), []);

  const { firstGrantorDeathName, secondGrantorDeathName } = useMemo(() => {
    const firstGrantorDeath = dispositionScenario?.firstGrantorDeath;
    return {
      firstGrantorDeathName: firstGrantorDeath?.displayName,
      secondGrantorDeathName: primaryClients.find(
        (g) => g.id !== firstGrantorDeath?.id
      )?.displayName,
    };
  }, [dispositionScenario?.firstGrantorDeath, primaryClients]);

  const { sections, cardHeading, rightHeading } = useMemo<{
    sections: SectionConfig[];
    cardHeading: string;
    rightHeading: string | null;
  }>(() => {
    if (isBdg) {
      const { primaryRows, contingentRows } =
        getBeneficiaryDesignationTableRows(beneficiaryDesignations);
      return {
        cardHeading: 'Beneficiary designations',
        rightHeading: null,
        sections: [
          { key: 'primary', heading: 'Primary', rows: primaryRows },
          { key: 'contingent', heading: 'Contingent', rows: contingentRows },
        ],
      };
    }
    return {
      cardHeading: 'Dispositions',
      rightHeading:
        dispositionScenario && primaryClients.length > 1
          ? `${dispositionScenario.firstGrantorDeath.displayName} dies first`
          : null,
      sections: (rowDataByDeath ?? []).map((rows, idx) => ({
        key: `${dispositionScenario?.id ?? 'no-scenario'}-${idx}`,
        heading: `Upon ${idx === 0 ? firstGrantorDeathName : secondGrantorDeathName}’s death`,
        rows,
      })),
    };
  }, [
    isBdg,
    beneficiaryDesignations,
    dispositionScenario,
    primaryClients.length,
    rowDataByDeath,
    firstGrantorDeathName,
    secondGrantorDeathName,
  ]);

  // BDG mode always renders; DP mode hides when rowDataByDeath is null.
  if (!isBdg && !rowDataByDeath) {
    return null;
  }

  const allSectionsEmpty = sections.every((s) => s.rows.length === 0);
  const dpMissingScenario = !isBdg && !dispositionScenario;
  if (allSectionsEmpty || dpMissingScenario) {
    return (
      <Card variant="outlined" sx={{ width: '100%', p: 2 }}>
        <Stack spacing={0.5} width="100%" alignItems="center">
          <Stack direction="row">
            <Typography variant="title-xs" component="span">
              {cardHeading}
            </Typography>
          </Stack>
          <Typography variant="body-xs">None specified</Typography>
        </Stack>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ width: '100%', p: 2 }}>
      <Stack spacing={2} width="100%">
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="title-xs" component="span">
            {cardHeading}
          </Typography>
          {rightHeading && (
            <Typography
              variant="title-s"
              component="span"
              color={COLORS.NEUTRAL_GRAY[400]}
            >
              {rightHeading}
            </Typography>
          )}
        </Stack>
        {sections.map((section) => {
          if (section.rows.length === 0) {
            // DP sections are skipped when empty; BDG sections render an
            // explicit "None specified" so users see both tiers.
            if (!isBdg) return null;
            return (
              <Stack key={section.key}>
                <Stack
                  direction="row"
                  p={1}
                  bgcolor={theme.palette.primary.main}
                >
                  <Typography
                    variant="title-xs"
                    component="span"
                    color={COLORS.PRIMITIVES.WHITE}
                  >
                    {section.heading}
                  </Typography>
                </Stack>
                <Stack p={1.5}>
                  <Typography variant="body-xs">None specified</Typography>
                </Stack>
              </Stack>
            );
          }
          return (
            <Stack key={section.key}>
              <Stack direction="row" p={1} bgcolor={theme.palette.primary.main}>
                <Typography
                  variant="title-xs"
                  component="span"
                  color={COLORS.PRIMITIVES.WHITE}
                >
                  {section.heading}
                </Typography>
              </Stack>
              <DataTable
                columns={columns}
                rows={section.rows}
                hideFooterPagination
                pagination={false}
                columnHeaderHeight={40}
                // Presentation-only, read-only table: the rows aren't clickable
                // and the slide is a static deliverable, so the default per-row
                // hover highlight and column sorting are both misleading affordances.
                rowHoverVariant="transparent"
                disableColumnSorting
                style={{
                  border: 'none',
                }}
              />
            </Stack>
          );
        })}
      </Stack>
    </Card>
  );
}
